import type {
  PhaseBuildBlockingReason,
  PhaseBuildExecutionState,
  PhaseBuildTaskOption,
} from '../../services/buildPhaseService';
import { getPhaseBuildTitle, isPhaseResolutionRequired } from '../../utils/phaseBuildPresentation';

import { creditRefusal, type PressRefusal } from './pressRefusal';

export const SELECT_TASKS_REFUSAL: PressRefusal = {
  code: 'no_tasks_selected',
  message: 'Select at least one task to build.',
};

export const TASK_PLAN_MISSING_REFUSAL: PressRefusal = {
  code: 'TASK_PLAN_MISSING',
  message: 'There is no task plan to build yet.',
};

type StateRefusal = (state: PhaseBuildExecutionState) => PressRefusal;

const STATE_REFUSALS: Partial<Record<PhaseBuildBlockingReason, StateRefusal>> = {
  insufficient_credits: (state) => creditRefusal(
    state.credit_shortfall, state.required_credits, state.available_credits,
  ),
  build_in_progress: () => ({ code: 'build_in_progress', message: 'This phase is already building.' }),
  task_plan_missing: () => TASK_PLAN_MISSING_REFUSAL,
  all_completed: () => ({ code: 'all_completed', message: 'Every build phase is complete.' }),
};

/** Why this phase state cannot start a build, or null when the press should go to the server. */
export const phaseStateRefusal = (state: PhaseBuildExecutionState | null): PressRefusal | null => {
  if (!state || state.can_start || state.blocking_reason === 'assignment_required') return null;
  if (isPhaseResolutionRequired(state)) return null;
  const reason = state.blocking_reason;

  return (reason && STATE_REFUSALS[reason]?.(state)) || { code: reason ?? 'phase_not_ready', message: getPhaseBuildTitle(state) };
};

const selectableTasks = (state: PhaseBuildExecutionState): PhaseBuildTaskOption[] => (
  (state.phase_tasks ?? []).filter((task) => task.selectable !== false && task.status !== 'done')
);

/**
 * An empty selection because the balance covers no task: say so with the cheapest task's price,
 * not "select a task" (with 0 credits the picker preselects nothing, so that was the answer).
 */
const unaffordableSelectionRefusal = (state: PhaseBuildExecutionState): PressRefusal | null => {
  const prices = selectableTasks(state).map((task) => task.estimated_credits).filter((price) => price > 0);
  const cheapest = prices.length ? Math.min(...prices) : 0;
  const available = Number(state.available_credits ?? 0);

  if (state.is_unlimited || !cheapest || cheapest <= available) return null;

  return creditRefusal(cheapest - available, cheapest, available);
};

export interface BuildPressCheck {
  state: PhaseBuildExecutionState | null;
  /** The phase task picker is open and nothing in it is selected. */
  selectionEmpty: boolean;
  /** What the selection (or the phase) still needs; 0 when the balance covers it. */
  creditShortfall: number;
}

/** What the bar answers at once, before any request: an empty selection, a known shortfall, a known blocker. */
export const buildPressPrecheck = (
  { state, selectionEmpty, creditShortfall }: BuildPressCheck,
): PressRefusal | null => {
  if (selectionEmpty) return (state && unaffordableSelectionRefusal(state)) || SELECT_TASKS_REFUSAL;
  if (creditShortfall > 0) {
    const available = Number(state?.available_credits ?? 0);

    return creditRefusal(creditShortfall, creditShortfall + available, available);
  }

  return phaseStateRefusal(state);
};

interface ReadinessStep {
  key: string;
  summary: string;
  action: string;
  label: string | null;
  credits: number | null;
  labels: string[];
}

/** The control for one listed step: credits for a credit block, the gate button for a planning step. */
const stepFix = (step: ReadinessStep): PressRefusal['fix'] => {
  if (step.action === 'add_credits') {
    return { kind: 'add_credits', label: 'Add Credits', ...(step.credits ? { credits: step.credits } : {}) };
  }
  if (step.action !== 'continue' || !step.label) return undefined;

  return { kind: 'prerequisite', label: step.label, labels: step.labels.length ? step.labels : [step.label] };
};

/**
 * "Before building: …" with every open step in order and one fix: the first step that has a
 * control (a step behind running work waits for it, so it gets none).
 */
export const prerequisitesRefusal = (base: PressRefusal, steps: ReadinessStep[]): PressRefusal => {
  if (!steps.length) return base;
  const first = steps[0];

  return {
    code: base.code,
    message: 'Before building:',
    steps: steps.map(({ key, summary }) => ({ key, summary })),
    ...(first.action === 'wait' ? {} : { fix: stepFix(first) }),
  };
};
